import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { IconWrapper } from '../common/IconWrapper';
import { IconType } from 'react-icons';

export interface Solution {
  id: string;
  title: string;
  description: string;
  icon: IconType;
  image: string;
  challenges: string[];
  solutions: string[];
  linkOverride?: string;
}

interface IndustrySolutionCardProps {
  solution: Solution;
  index: number;
}

export const IndustrySolutionCard: React.FC<IndustrySolutionCardProps> = ({ solution, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 h-full flex flex-col max-w-md mx-auto">
        <div className="relative h-32 overflow-hidden">
          <img
            src={solution.image}
            alt={solution.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/10 via-transparent to-slate-900/30 dark:from-slate-900/40 dark:via-transparent dark:to-slate-900/60" />
          <div className="absolute top-3 left-3 z-10 p-1.5 rounded-lg bg-white/90 dark:bg-gray-800/90 shadow-lg">
            <IconWrapper icon={solution.icon} className="w-5 h-5 text-primary-light dark:text-primary-dark" />
          </div>
        </div>
        <div className="p-4 flex-1 flex flex-col">
          <h3 className="text-lg font-semibold mb-1.5 group-hover:text-primary-light dark:group-hover:text-primary-dark">
            {solution.title}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
            {solution.description}
          </p>

          <div className="space-y-2.5 flex-1">
            <div>
              <h4 className="text-sm font-semibold mb-1.5 text-slate-900 dark:text-white">Key Challenges</h4>
              <ul className="space-y-1">
                {solution.challenges.slice(0, 2).map((challenge, i) => (
                  <li key={i} className="text-xs text-gray-600 dark:text-gray-400 flex items-start">
                    <span className="mr-1.5 mt-1">•</span>
                    <span>{challenge}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-1.5 text-slate-900 dark:text-white">Our Solutions</h4>
              <ul className="space-y-1">
                {solution.solutions.slice(0, 2).map((sol, i) => (
                  <li key={i} className="text-xs text-gray-600 dark:text-gray-400 flex items-start">
                    <span className="mr-1.5 mt-1">•</span>
                    <span>{sol}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <Link
            to={solution.linkOverride ?? `/solutions/${solution.id}`}
            className="mt-3 inline-flex items-center text-sm text-primary-light dark:text-primary-dark hover:underline font-medium"
          >
            Learn More
            <svg
              className="ml-1 w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        </div>
      </div>
    </motion.div>
  );
};